import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bookmark,
  ExternalLink,
  Trash2,
  FileText,
  Newspaper,
  Lightbulb,
  DollarSign,
  CalendarDays,
  Pencil,
  Check,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { formatDistanceToNow } from "date-fns";
import {
  useSavedItems,
  type SavedItem,
  type SavedItemType,
} from "@/hooks/useSavedItems";
import {
  useSavedOpportunities,
  type SavedOpportunity,
} from "@/hooks/useSavedOpportunities";

interface SavedTabProps {
  onCountChange?: (count: number) => void;
}

type Filter = "all" | SavedItemType;

const FILTERS: { value: Filter; label: string; icon: any }[] = [
  { value: "all", label: "All", icon: Bookmark },
  { value: "article", label: "Articles", icon: FileText },
  { value: "curated_post", label: "Posts", icon: Newspaper },
  { value: "idea", label: "Ideas", icon: Lightbulb },
  { value: "opportunity", label: "Opportunities", icon: DollarSign },
  { value: "event", label: "Events", icon: CalendarDays },
];

const typeLabel = (t: SavedItemType) =>
  FILTERS.find((f) => f.value === t)?.label.replace(/s$/, "") ?? t;

const emptyCopy = (f: Filter) => {
  switch (f) {
    case "article":
      return { msg: "Save articles from Insights to revisit them later.", cta: "Browse Insights", href: "/insights" };
    case "curated_post":
      return { msg: "Save curated posts from Happening Now to revisit them later.", cta: "Open Insights", href: "/insights" };
    case "idea":
      return { msg: "Save ideas from the Ideas Board to track them.", cta: "Open Ideas", href: "/ideas" };
    case "opportunity":
      return { msg: "Save grants and funding opportunities to revisit them.", cta: "Find opportunities", href: "/opportunities" };
    case "event":
      return { msg: "Save events you want to attend.", cta: "Browse Events", href: "/events" };
    default:
      return { msg: "Anything you save across The Emerging Market lives here.", cta: "Browse Insights", href: "/insights" };
  }
};

/** Legacy `saved_opportunities` row → normalized SavedItem-like view. */
const legacyToView = (it: SavedOpportunity): SavedItem & { __legacy: true } => ({
  __legacy: true,
  id: it.id,
  user_id: it.user_id,
  item_type: "opportunity",
  item_id: it.fingerprint,
  title: it.title,
  url: it.url,
  source: it.source ?? it.source_type ?? null,
  cover_image_url: null,
  summary: it.description ?? null,
  note: it.notes,
  metadata: {
    amount: it.amount,
    deadline: it.deadline,
    status: it.status,
    matched_area: it.matched_area,
  },
  created_at: it.created_at,
  updated_at: it.updated_at,
});

const SavedTab = ({ onCountChange }: SavedTabProps) => {
  const { items, loading, removeById, updateNote } = useSavedItems();
  const { items: legacy, loading: legacyLoading, removeById: removeLegacy } =
    useSavedOpportunities();
  const [filter, setFilter] = useState<Filter>("all");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftNote, setDraftNote] = useState("");

  // Merge new saved_items + any legacy opportunity rows that aren't already mirrored.
  const merged: (SavedItem & { __legacy?: boolean })[] = useMemo(() => {
    const seen = new Set(
      items
        .filter((i) => i.item_type === "opportunity")
        .map((i) => i.item_id),
    );
    const extras = legacy
      .filter((l) => !seen.has(l.fingerprint))
      .map(legacyToView);
    return [...items, ...extras].sort(
      (a, b) => +new Date(b.created_at) - +new Date(a.created_at),
    );
  }, [items, legacy]);

  useEffect(() => {
    if (!loading && !legacyLoading) onCountChange?.(merged.length);
  }, [merged.length, loading, legacyLoading, onCountChange]);

  const filtered = useMemo(
    () => (filter === "all" ? merged : merged.filter((i) => i.item_type === filter)),
    [merged, filter],
  );

  const counts = useMemo(() => {
    const map = new Map<Filter, number>();
    map.set("all", merged.length);
    for (const f of FILTERS) {
      if (f.value === "all") continue;
      map.set(f.value, merged.filter((i) => i.item_type === f.value).length);
    }
    return map;
  }, [merged]);

  const handleRemove = (item: SavedItem & { __legacy?: boolean }) => {
    if (item.__legacy) void removeLegacy(item.id);
    else void removeById(item.id);
  };

  const beginEdit = (item: SavedItem) => {
    setEditingId(item.id);
    setDraftNote(item.note ?? "");
  };

  const saveNote = async (id: string) => {
    await updateNote(id, draftNote.trim());
    setEditingId(null);
  };

  if (loading || legacyLoading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-24 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (merged.length === 0) {
    const e = emptyCopy("all");
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border p-10 text-center">
        <Bookmark className="h-6 w-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground max-w-md">{e.msg}</p>
        <Link to={e.href}>
          <Button size="sm" variant="outline">{e.cta}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => {
          const c = counts.get(f.value) ?? 0;
          if (f.value !== "all" && c === 0) return null;
          const active = filter === f.value;
          const Icon = f.icon;
          return (
            <Button
              key={f.value}
              size="sm"
              variant={active ? "default" : "outline"}
              className={active ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}
              onClick={() => setFilter(f.value)}
            >
              <Icon className="h-3.5 w-3.5 mr-1.5" />
              {f.label}
              <span className="ml-1.5 text-xs opacity-80">{c}</span>
            </Button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        (() => {
          const e = emptyCopy(filter);
          return (
            <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              <p>{e.msg}</p>
              <Link to={e.href} className="inline-block mt-3">
                <Button size="sm" variant="outline">{e.cta}</Button>
              </Link>
            </div>
          );
        })()
      ) : (
        <div className="space-y-3">
          {filtered.map((it) => {
            const internal = it.url && it.url.startsWith("/");
            const meta = it.metadata as Record<string, any>;
            return (
              <div key={`${it.__legacy ? "legacy" : "new"}-${it.id}`} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {typeLabel(it.item_type)}
                      </Badge>
                      {it.source ? (
                        <span className="text-[11px] text-muted-foreground">{it.source}</span>
                      ) : null}
                      <span className="text-[11px] text-muted-foreground">
                        Saved {formatDistanceToNow(new Date(it.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <h3 className="font-display text-sm font-semibold text-foreground leading-snug">
                      {it.title}
                    </h3>
                    {it.summary ? (
                      <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{it.summary}</p>
                    ) : null}
                    {(meta?.amount || meta?.deadline) && (
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                        {meta.amount ? (
                          <span className="inline-flex items-center gap-1 text-accent">
                            <DollarSign className="h-3 w-3" />
                            {meta.amount}
                          </span>
                        ) : null}
                        {meta.deadline ? (
                          <span className="inline-flex items-center gap-1">
                            <CalendarDays className="h-3 w-3" />
                            {meta.deadline}
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    {it.url ? (
                      internal ? (
                        <Link
                          to={it.url}
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          Open <ExternalLink className="h-3 w-3" />
                        </Link>
                      ) : (
                        <a
                          href={it.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          Open <ExternalLink className="h-3 w-3" />
                        </a>
                      )
                    ) : null}
                  </div>
                </div>

                {/* Note editor (new saved_items only — legacy notes editing not supported here) */}
                {!it.__legacy && (
                  <div className="mt-3 border-t border-border pt-3">
                    {editingId === it.id ? (
                      <div className="space-y-2">
                        <Textarea
                          value={draftNote}
                          onChange={(e) => setDraftNote(e.target.value)}
                          rows={2}
                          placeholder="Add a quick note for yourself…"
                          className="text-xs"
                        />
                        <div className="flex gap-1.5">
                          <Button size="sm" className="h-7 px-2.5 text-xs" onClick={() => saveNote(it.id)}>
                            <Check className="h-3 w-3 mr-1" /> Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2.5 text-xs"
                            onClick={() => setEditingId(null)}
                          >
                            <X className="h-3 w-3 mr-1" /> Cancel
                          </Button>
                        </div>
                      </div>
                    ) : it.note ? (
                      <button
                        onClick={() => beginEdit(it)}
                        className="text-xs text-muted-foreground italic text-left hover:text-foreground transition-colors"
                      >
                        <Pencil className="inline h-3 w-3 mr-1" />
                        {it.note}
                      </button>
                    ) : (
                      <button
                        onClick={() => beginEdit(it)}
                        className="text-[11px] text-muted-foreground hover:text-primary inline-flex items-center"
                      >
                        <Pencil className="h-3 w-3 mr-1" /> Add a note
                      </button>
                    )}
                  </div>
                )}

                <div className="mt-3 flex justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2.5 text-xs text-muted-foreground hover:text-destructive"
                    onClick={() => handleRemove(it)}
                  >
                    <Trash2 className="mr-1 h-3.5 w-3.5" />
                    Remove
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SavedTab;